/**
 * What a learner (and the HR "Learners" tab) is shown for an assignment — training redesign §5.
 *
 * Six stored statuses collapse to four plain words; the due date adds an overlay chip. Overdue is
 * never a status of its own (D2 of the original plan): a course can be "In progress" AND overdue.
 * Pure, so the API's list filters and every client render the same answer.
 */

import type { TrainingAssignmentStatus, TrainingLearnerStatus } from '../enums.js';
import { TRAINING_DUE_SOON_LEAD_MINUTES } from './display-status.js';

export type TrainingDueOverlay = 'overdue' | 'due_soon' | null;

export interface TrainingLearnerViewInput {
  readonly status: TrainingAssignmentStatus;
  /** `null` = no due date: never overdue or due soon. */
  readonly dueAt: Date | null;
  readonly overrunMinutes: number | null;
}

export interface TrainingLearnerView {
  /** `null` for a cancelled assignment — it is hidden from the learner and shown as "Removed" to HR. */
  readonly status: TrainingLearnerStatus | null;
  readonly overlay: TrainingDueOverlay;
  /** Completed after its due date. */
  readonly late: boolean;
}

const LEARNER_STATUS: Record<TrainingAssignmentStatus, TrainingLearnerStatus | null> = {
  not_started: 'not_started',
  started: 'in_progress',
  in_progress: 'in_progress',
  completed: 'completed',
  incomplete: 'not_completed',
  cancelled: null,
};

export function trainingLearnerView(input: TrainingLearnerViewInput, now: Date): TrainingLearnerView {
  const status = LEARNER_STATUS[input.status];
  const late = input.status === 'completed' && (input.overrunMinutes ?? 0) > 0;
  const open = status === 'not_started' || status === 'in_progress';
  if (!open || input.dueAt === null) return { status, overlay: null, late };

  const msLeft = input.dueAt.getTime() - now.getTime();
  if (msLeft <= 0) return { status, overlay: 'overdue', late };
  if (msLeft <= TRAINING_DUE_SOON_LEAD_MINUTES * 60_000) return { status, overlay: 'due_soon', late };
  return { status, overlay: null, late };
}

/** Learner-facing label, in the redesign's vocabulary. */
export const TRAINING_LEARNER_STATUS_LABELS: Record<TrainingLearnerStatus, string> = {
  not_started: 'Not started',
  in_progress: 'In progress',
  completed: 'Completed',
  not_completed: 'Not completed',
};
